import { FC, useCallback, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import { age, altitudeLevel, cloudCover, distanceToAero, flightType, Months, sex, visibilitySelector, windDirectionsSpanish, windSpeed, yesNoSelector } from "../../../../utils/SelectorMapping";
import { usePrivilege } from "../../../../components/priviledge/PriviledgeProvider";
import { CensusService } from "../../../../services/census/censusService";
import WidgetCard from "../../../../components/card/WidgetCard";
import { 
    PiCalendarDuotone, 
    PiClockDuotone, 
    PiUserDuotone, 
    PiMapPinDuotone, 
    PiBirdDuotone, 
    PiWindDuotone,
    PiCloudDuotone,
    PiEyeDuotone,
    PiCompassDuotone,
    PiGaugeDuotone,
    PiWarningDuotone,
    PiNoteDuotone,
    PiHashDuotone,
    PiGenderIntersexDuotone,
    PiTimerDuotone,
    PiArrowsClockwiseDuotone,
    PiFlowArrowDuotone,
    PiAirplaneTiltDuotone,
    PiLightningDuotone,
    PiRulerDuotone,
    PiIdentificationBadgeDuotone,
    PiBuildingOfficeDuotone,
    PiTagDuotone,
    PiCaretDownDuotone,
    PiCaretRightDuotone
} from "react-icons/pi";

import { Badge, Loader, Text, Title } from "rizzui";

interface CensusInfoProps {
    
}

interface InfoCardProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

const InfoCard: FC<InfoCardProps> = ({ title, icon, children, className = "" }) => (
    <WidgetCard 
        title={
            <div className="flex items-center gap-2">
                <div className="text-primary">{icon}</div>
                <span className="text-gray-900 font-semibold">{title}</span>
            </div>
        }
        className={`border border-gray-200 hover:shadow-md transition-shadow duration-200 ${className}`}
        titleClassName="text-sm"
    >
        <div className="mt-4 space-y-3">
            {children}
        </div>
    </WidgetCard>
);

interface DataRowProps {
    label: string;
    value: string | number | null | undefined;
    icon?: React.ReactNode;
    badge?: boolean;
    italic?: boolean;
    customBadgeColor?: string;
    customClassName?: string;
}

const DataRow: FC<DataRowProps> = ({ label, value, icon, badge = false, italic = false, customBadgeColor = null, customClassName = ''}) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2 text-gray-600">
            {icon && <span className="text-gray-400">{icon}</span>}
            <Text className="font-medium text-sm">{label}</Text>
        </div>
        <div className="text-right">
            {badge ? (
                <Badge variant="flat" className={`text-xs text-white ${customClassName}`} style={{ backgroundColor: customBadgeColor || '#007bff' }}>
                    {value || 'Sin datos'}
                </Badge>
            ) : (
                <Text className={`text-sm text-gray-900 ${italic ? 'italic' : ''} ${customClassName}`}>
                    {value || 'Sin datos'}
                </Text>
            )}
        </div>
    </div>
);

const CensusInfo: FC<CensusInfoProps> = () => {
    
    // STATES 

    const [isRefecth, setIsRefecth] = useState<boolean>(true);
    const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set());

    // HOOKS

    const {id  = ''} = useParams<{ id: string }>();
    const service = new CensusService();
    const { userCan } = usePrivilege();

    // FUNCTIONS

    const toggleLineExpansion = (lineIndex: number) => {
        const newExpandedLines = new Set(expandedLines);
        if (newExpandedLines.has(lineIndex)) {
            newExpandedLines.delete(lineIndex);
        } else {
            newExpandedLines.add(lineIndex);
        }
        setExpandedLines(newExpandedLines);
    };

    const expandAllLines = () => {
        if (data?.censusLines) {
            const allIndices: Set<number> = new Set();
            data.censusLines.forEach((_: any, index: number) => {
                allIndices.add(index);
            });
            setExpandedLines(allIndices);
        }
    };

    const collapseAllLines = () => {
        setExpandedLines(new Set());
    };

    // DATA FETCHING

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '' || isRefecth == false) return null;
        if (isRefecth) setIsRefecth(false);
        const response = await service.getCensusById(id);
        return response.getResponseData() as any;
    }, [id, isRefecth]));
    
    const [isDataReady, setIsDataReady] = useState(false);
    
    useEffect(() => {
        if (!loading && data) {
            const timer = setTimeout(() => {
                setIsDataReady(true);
            }, 100);
            return () => clearTimeout(timer);
        } else if (loading) {
            setIsDataReady(false);
        }
    }, [loading, data]);
    
    if (loading || !isDataReady) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader variant="spinner" size="xl" />
            </div>
        );
    }

    // RENDER 

    return (
        <div className="@container space-y-6">
            {/* Header con información principal del censo */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-start justify-between">
                    <div>
                        <Title as="h2" className="text-2xl font-bold text-gray-900 mb-2">
                            Vista de Censo
                        </Title>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                                <PiCalendarDuotone className="w-4 h-4" />
                                <span>
                                    {data?.date ? new Date(data?.date?.date).toLocaleDateString('es-ES', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    }) : 'Sin fecha'}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <PiClockDuotone className="w-4 h-4" />
                                <span>
                                    {data?.time ? new Date(data?.time?.date).toLocaleTimeString('es-ES', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }) : 'N/A'} - 
                                    {data?.endTime ? new Date(data?.endTime?.date).toLocaleTimeString('es-ES', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }) : 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <Badge variant="flat" color="primary" className="mb-2">
                            {data?.park?.name || 'Parque no especificado'}
                        </Badge>
                        <div className="text-sm text-gray-600">
                            Observador: {data?.user?.name || 'N/A'} {data?.user?.lastName || ''}
                        </div>
                    </div>
                </div>
            </div>

            {/* Información General del Censo */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                
                {/* Información General */}
                <InfoCard 
                    title="Información General del Censo" 
                    icon={<PiIdentificationBadgeDuotone className="w-5 h-5" />}
                >
                    {userCan('list','companies') && (
                        <DataRow 
                            label="Compañía" 
                            value={data?.company?.name}
                            icon={<PiBuildingOfficeDuotone className="w-4 h-4" />}
                        />
                    )}
                    <DataRow 
                        label="Estado" 
                        value={data?.status?.name}
                        customBadgeColor={data?.status?.color}
                        icon={<PiGaugeDuotone className="w-4 h-4" />}
                        badge
                    />
                    <DataRow 
                        label="Puesto de Observación" 
                        value={data?.observationPost}
                        icon={<PiMapPinDuotone className="w-4 h-4" />}
                    />
                    <DataRow 
                        label="Censo DPMA" 
                        value={data?.censusDpma}
                        icon={<PiNoteDuotone className="w-4 h-4" />}
                    />
                </InfoCard>

                {/* Información de Ubicación */}
                <InfoCard 
                    title="Ubicación" 
                    icon={<PiMapPinDuotone className="w-5 h-5" />}
                >
                    <DataRow 
                        label="Parque Eólico" 
                        value={data?.park?.name}
                        icon={<PiMapPinDuotone className="w-4 h-4" />}
                        badge
                        customBadgeColor="#28a745"
                    />
                    <DataRow 
                        label="Usuario" 
                        value={`${data?.user?.name || ''} ${data?.user?.lastName || ''}`.trim()}
                        icon={<PiUserDuotone className="w-4 h-4" />}
                    />
                </InfoCard>

                {/* Información Temporal */}
                <InfoCard 
                    title="Información Temporal" 
                    icon={<PiClockDuotone className="w-5 h-5" />}
                >
                    <DataRow 
                        label="Fecha de Creación" 
                        value={data?.createdAt ? new Date(data?.createdAt?.date).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }) : 'Sin fecha'}
                        icon={<PiCalendarDuotone className="w-4 h-4" />}
                    />
                    <DataRow 
                        label="Última Modificación" 
                        value={data?.updatedAt ? new Date(data?.updatedAt?.date).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }) : 'Sin fecha'}
                        icon={<PiCalendarDuotone className="w-4 h-4" />}
                    />
                </InfoCard>
            </div>

            {/* Líneas de Censo */}
            {data?.censusLines && data.censusLines.length > 0 && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <PiBirdDuotone className="w-6 h-6 text-primary" />
                            <Title as="h3" className="text-xl font-bold text-gray-900">
                                Líneas de Censo ({data.censusLines.length})
                            </Title>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={expandAllLines}
                                className="px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-200"
                            >
                                Expandir Todo
                            </button>
                            <button
                                onClick={collapseAllLines}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                            >
                                Contraer Todo
                            </button>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4">
                        {data.censusLines.map((line: any, index: number) => {
                            const isExpanded = expandedLines.has(index);
                            
                            return (
                                <div key={line.id || index} className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
                                    {/* Header de la línea - Siempre visible */}
                                    <div 
                                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                                        onClick={() => toggleLineExpansion(index)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                                {isExpanded ? (
                                                    <PiCaretDownDuotone className="w-4 h-4 text-gray-500 transition-transform duration-200" />
                                                ) : (
                                                    <PiCaretRightDuotone className="w-4 h-4 text-gray-500 transition-transform duration-200" />
                                                )}
                                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                    <Text className="text-sm font-bold text-primary">{index + 1}</Text>
                                                </div>
                                            </div>
                                            <div>
                                                <Title as="h4" className="text-lg font-semibold text-gray-900">
                                                    Línea {index + 1}
                                                    {line.species?.name && (
                                                        <span className="ml-2 text-sm font-normal text-gray-600">
                                                            - {line.species.name}
                                                        </span>
                                                    )}
                                                </Title>
                                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                                    {line.time && (
                                                        <span>
                                                            Hora: {line.time ? new Date(line.time?.date).toLocaleTimeString('es-ES', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            }) : 'N/A'}
                                                        </span>
                                                    )}
                                                    {line.numIndividuals && (
                                                        <span>
                                                            {line.numIndividuals} individuo{line.numIndividuals > 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                    {line.windturbine?.name && (
                                                        <span>
                                                            {line.windturbine.name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {line.riskDetected && (
                                                <Badge variant="flat" color="danger" className="text-xs">
                                                    <PiWarningDuotone className="w-3 h-3 mr-1" />
                                                    Riesgo
                                                </Badge>
                                            )}
                                            <Badge 
                                                variant="flat" 
                                                color={line.aeroStatus === 'encendido' ? 'success' : 'warning'} 
                                                className="text-xs"
                                            >
                                                <PiGaugeDuotone className="w-3 h-3 mr-1" />
                                                {line.aeroStatus?.charAt(0).toUpperCase() + line.aeroStatus?.slice(1) || 'N/A'}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Contenido expandible de la línea */}
                                    <div className={`transition-all duration-300 ease-in-out ${
                                        isExpanded 
                                            ? 'max-h-[2000px] opacity-100' 
                                            : 'max-h-0 opacity-0 overflow-hidden'
                                    }`}>
                                        <div className="px-4 pb-4 border-t border-gray-100">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
                                                
                                                {/* Información de Especie */}
                                                <InfoCard 
                                                    title="Información de Especie" 
                                                    icon={<PiBirdDuotone className="w-5 h-5" />}
                                                    className="h-fit"
                                                >
                                                    <DataRow 
                                                        label="Especie" 
                                                        value={line.species?.name || line.species?.scientificName}
                                                        icon={<PiBirdDuotone className="w-4 h-4" />}
                                                        italic
                                                        badge
                                                        customBadgeColor="#6c757d"
                                                        customClassName="italic"
                                                    />
                                                    <DataRow 
                                                        label="Número de Individuos" 
                                                        value={line.numIndividuals}
                                                        icon={<PiHashDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Sexo" 
                                                        value={line.sex ? sex.find(option => option.value == line.sex)?.label : 'Sin datos'}
                                                        icon={<PiGenderIntersexDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Edad" 
                                                        value={line.age ? age.find(option => option.value == line.age)?.label : 'Sin datos'}
                                                        icon={<PiTimerDuotone className="w-4 h-4" />}
                                                    />
                                                </InfoCard>

                                                {/* Condiciones Meteorológicas */}
                                                <InfoCard 
                                                    title="Condiciones Meteorológicas" 
                                                    icon={<PiCloudDuotone className="w-5 h-5" />}
                                                    className="h-fit"
                                                >
                                                    <DataRow 
                                                        label="Dirección del Viento" 
                                                        value={line.windDirection ? windDirectionsSpanish.find(option => option.value == line.windDirection)?.label : 'Sin datos'}
                                                        icon={<PiCompassDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Velocidad del Viento" 
                                                        value={line.windSpeed !== null ? windSpeed.find(option => option.value == String(line.windSpeed))?.label : 'Sin datos'}
                                                        icon={<PiWindDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Cobertura de Nubes" 
                                                        value={line.cloudCoverage !== null ? cloudCover.find(option => option.value == String(line.cloudCoverage))?.label : 'Sin datos'}
                                                        icon={<PiCloudDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Visibilidad" 
                                                        value={line.visibility !== null ? visibilitySelector.find(option => option.value == String(line.visibility))?.label : 'Sin datos'}
                                                        icon={<PiEyeDuotone className="w-4 h-4" />}
                                                    />
                                                    {line.rain && (
                                                        <DataRow 
                                                            label="Lluvia" 
                                                            value={line.rain}
                                                            icon={<PiCloudDuotone className="w-4 h-4" />}
                                                        />
                                                    )}
                                                </InfoCard>

                                                {/* Información de Aerogenerador */}
                                                <InfoCard 
                                                    title="Aerogenerador y Ubicación" 
                                                    icon={<PiLightningDuotone className="w-5 h-5" />}
                                                    className="h-fit"
                                                >
                                                    <DataRow 
                                                        label="Aerogenerador" 
                                                        value={line.windturbine?.name || line.windturbine?.alias}
                                                        icon={<PiLightningDuotone className="w-4 h-4" />}
                                                        badge
                                                        customBadgeColor="#007bff"
                                                    />
                                                    <DataRow 
                                                        label="Distancia al Aerogenerador" 
                                                        value={line.distanceToAero ? distanceToAero.find(option => option.value == line.distanceToAero)?.label : 'Sin datos'}
                                                        icon={<PiRulerDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Altitud" 
                                                        value={line.altitudLevel ? altitudeLevel.find(option => option.value == line.altitudLevel)?.label : 'Sin datos'}
                                                        icon={<PiArrowsClockwiseDuotone className="w-4 h-4" />}
                                                    />
                                                </InfoCard>

                                                {/* Comportamiento de Vuelo */}
                                                <InfoCard 
                                                    title="Comportamiento de Vuelo" 
                                                    icon={<PiAirplaneTiltDuotone className="w-5 h-5" />}
                                                    className="h-fit"
                                                >
                                                    <DataRow 
                                                        label="Dirección de Vuelo" 
                                                        value={line.flightDirection}
                                                        icon={<PiFlowArrowDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Tipo de Vuelo" 
                                                        value={line.flightType ? flightType.find(option => option.value == line.flightType)?.label : 'Sin datos'}
                                                        icon={<PiAirplaneTiltDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Dirección Origen" 
                                                        value={line.dirOrigen ? windDirectionsSpanish.find(option => option.value === line.dirOrigen)?.label : 'Sin datos'}
                                                        icon={<PiCompassDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Dirección Destino" 
                                                        value={line.dirDest ? windDirectionsSpanish.find(option => option.value === line.dirDest)?.label : 'Sin datos'}
                                                        icon={<PiCompassDuotone className="w-4 h-4" />}
                                                    />
                                                    <DataRow 
                                                        label="Reacción (REACC)" 
                                                        value={line.reacc ? yesNoSelector.find(option => option.value === line.reacc)?.label : 'Sin datos'}
                                                        icon={<PiArrowsClockwiseDuotone className="w-4 h-4" />}
                                                    />
                                                </InfoCard>

                                                {/* Información Adicional */}
                                                <InfoCard 
                                                    title="Información Adicional" 
                                                    icon={<PiNoteDuotone className="w-5 h-5" />}
                                                    className="h-fit"
                                                >
                                                    <DataRow 
                                                        label="Solicitar Anillado" 
                                                        value={line.askAnillado ? yesNoSelector.find(option => option.value == line.askAnillado)?.label : 'Sin datos'}
                                                        icon={<PiTagDuotone className="w-4 h-4" />}
                                                    />
                                                    {line.extraAnillado && (
                                                        <DataRow 
                                                            label="Anillado" 
                                                            value={line.extraAnillado}
                                                            icon={<PiTagDuotone className="w-4 h-4" />}
                                                        />
                                                    )}
                                                    <DataRow 
                                                        label="Solicitar Parada" 
                                                        value={line.askStop ? yesNoSelector.find(option => option.value === line.askStop)?.label : 'Sin datos'}
                                                        icon={<PiEyeDuotone className="w-4 h-4" />}
                                                    />
                                                    {line.anillado && (
                                                        <DataRow 
                                                            label="Observaciones" 
                                                            value={line.anillado}
                                                            icon={<PiTagDuotone className="w-4 h-4" />}
                                                        />
                                                    )}
                                                    {line.aeroOnComments && (
                                                        <DataRow 
                                                            label="Comentarios Aerogenerador" 
                                                            value={line.aeroOnComments}
                                                            icon={<PiLightningDuotone className="w-4 h-4" />}
                                                        />
                                                    )}
                                                    <DataRow 
                                                        label="Minutos de Censo" 
                                                        value={line.censusMin}
                                                        icon={<PiTimerDuotone className="w-4 h-4" />}
                                                    />
                                                </InfoCard>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Mensaje si no hay líneas */}
            {(!data?.censusLines || data.censusLines.length === 0) && (
                <WidgetCard 
                    title={
                        <div className="flex items-center gap-2">
                            <PiBirdDuotone className="w-5 h-5 text-primary" />
                            <span className="text-gray-900 font-semibold">Líneas de Censo</span>
                        </div>
                    }
                    className="border border-gray-200"
                >
                    <div className="text-center py-12">
                        <PiBirdDuotone className="mx-auto w-16 h-16 text-gray-300 mb-4" />
                        <Title as="h4" className="text-lg font-medium text-gray-500 mb-2">
                            No hay líneas de censo registradas
                        </Title>
                        <Text className="text-gray-400">
                            Las líneas de censo contienen los datos específicos de cada observación de aves
                        </Text>
                    </div>
                </WidgetCard>
            )}
        </div>
    )
}

export default CensusInfo;